import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';

// ============================================================
// TYPES
// ============================================================

interface AgentStatus {
  agent_id: string;
  name: string;
  enabled: boolean;
  last_run_at: string | null;
  last_run_status: string;
  products_processed_last_run: number;
  error_count_last_run: number;
  next_scheduled_run: string | null;
  total_products_processed: number;
  total_runs: number;
}

interface AgentHealth {
  status: string;
  last_heartbeat: string | null;
  error_rate: number;
  circuit_breaker_state: string;
  consecutive_failures: number;
  products_processed_total: number;
}

interface ProductSearchResult {
  id: number;
  ndc_code: string;
  product_name: string;
  generic_name?: string;
  manufacturer?: string;
  manufacturer_name?: string;
  api_source_country?: string;
  country_of_origin?: string;
}

// ============================================================
// AGENT METADATA (mirrors dashboard)
// ============================================================

interface DataSourceDef {
  name: string;
  category?: 'structured' | 'ai-dependent';
  factors: { key: string; label: string }[];
}

interface AgentMeta {
  display: string;
  type: string;
  description: string;
  dataSources: DataSourceDef[];
}

// Steward agent metadata — mirrors STEWARD_META from AgentActivityDashboard
const STEWARD_META: Record<string, { display: string; type: string; description: string }> = {
  steward_fda_establishments: { display: 'FDA Data Dashboard', type: 'FDA Inspection Data', description: 'Queries FDA Data Dashboard API for inspection and classification records.' },
  steward_openfda_ndc: { display: 'OpenFDA NDC', type: 'Drug Product Enrichment', description: 'Enriches product records with NDC directory data from OpenFDA API.' },
  steward_fda_import_refusals: { display: 'FDA Import Refusals', type: 'Import Alerts', description: 'Collects import refusal records to identify foreign manufacturing signals.' },
  steward_epa_tri: { display: 'EPA TRI', type: 'Toxic Release Inventory', description: 'Collects EPA Toxic Release Inventory data for manufacturing facility identification.' },
  steward_fda_recalls: { display: 'FDA Recalls', type: 'Recall Events', description: 'Collects FDA recall and enforcement data for safety signal tracking.' },
  steward_dailymed_spl: { display: 'DailyMed SPL', type: 'Structured Product Labels', description: 'Collects and analyzes DailyMed SPL documents for COO extraction.' },
  steward_package_ocr: { display: 'Package OCR', type: 'Vision API Label Extraction', description: 'Extracts manufacturing information from package images using Vision API.' },
  steward_sec_edgar: { display: 'SEC EDGAR', type: 'Corporate Filings', description: 'Analyzes SEC EDGAR filings for manufacturing facility disclosures.' },
  steward_drugs_at_fda: { display: 'Drugs@FDA', type: 'Approval History', description: 'Collects drug application approval history from Drugs@FDA database.' },
  steward_fda_registration: { display: 'FDA Registration', type: 'Site Registration', description: 'Collects FDA site registration and FEI lookup data.' },
  steward_who_prequalification: { display: 'WHO Prequalification', type: 'WHO PQ List', description: 'Collects WHO Prequalification data for global quality assessment.' },
  steward_dmf_pharmacompass: { display: 'DMF PharmaCompass', type: 'Drug Master Files', description: 'Collects Drug Master File data from PharmaCompass for API source identification.' },
  steward_fda_ndc_directory: { display: 'FDA NDC Directory', type: 'NDC Product Data', description: 'Bulk product metadata import from FDA NDC Directory (brand name, generic name, dosage form, etc.).' },
  steward_fda_drug_shortages: { display: 'FDA Drug Shortages', type: 'Active Shortages', description: 'Bulk shortage alert data — tracks active shortages, reasons, and resolution status.' },
  steward_pricing: { display: 'Pricing (NADAC)', type: 'CMS NADAC', description: 'Collects CMS NADAC acquisition cost data for pricing intelligence.' },
  steward_rxnorm: { display: 'RxNorm', type: 'Clinical Drug', description: 'Collects RxNorm clinical drug and therapeutic class data.' },
  steward_fda_label_api: { display: 'FDA Label API', type: 'Drug Labels', description: 'Collects drug label SPL content from FDA Label API.' },
  steward_haiku_spl: { display: 'Haiku SPL Analysis', type: 'LLM Analysis', description: 'LLM-based analysis of SPL documents for COO extraction using Claude Haiku.' },
  steward_legislation: { display: 'Legislation', type: 'Pharma Bills', description: 'Tracks pharmaceutical-related legislation from Congress.gov.' },
  steward_fec_donations: { display: 'FEC Donations', type: 'Political Donations', description: 'Collects FEC political donation data for manufacturer executives.' },
  steward_lobbying: { display: 'Lobbying (Senate LDA)', type: 'Lobby Disclosures', description: 'Collects Senate LDA lobbying disclosure filings for manufacturers.' },
  steward_politician_holdings: { display: 'Politician Holdings', type: 'Stock Trades', description: 'Collects politician stock trades and financial disclosure data for pharma companies.' },
  steward_pac_contributions: { display: 'PAC Contributions', type: 'PAC Spending', description: 'Collects PAC spending and political action committee contribution data.' },
  steward_ccp_flags: { display: 'CCP Flags', type: 'China Exposure', description: 'Screens manufacturers against DOD 1260H, NS-CMIC, UFLPA, and OFAC SDN lists.' },
  steward_eudra_gmdp: { display: 'EudraGMDP', type: 'EU GMP Compliance', description: 'Queries EMA GMP Compliance Database for manufacturing site certificates.' },
};

const AGENT_META: Record<string, AgentMeta> = {
  coo_scoring: {
    display: 'COO Scoring Agent',
    type: 'Country of Origin',
    description: 'Additive flat-factor COO scoring — 16 data sources (13 structured + 3 AI-optional) with 59 boolean factors. 10 existence/status factors are disabled from scoring (cannot confirm a specific COO). Enabled factors weighted evenly: 100% / number_of_enabled_factors. Determines country of origin and confidence tier per product. Tiers: HIGH >= 70, MODERATE 40-69, LOW < 40.',
    dataSources: [
      // ── 13 Structured sources (always enabled) ──
      {
        name: 'OpenFDA NDC',
        category: 'structured',
        factors: [
          { key: 'product_found_in_ndc_directory', label: 'Product found in FDA NDC directory' },
          { key: 'manufacturer_labeler_identified', label: 'Manufacturer/labeler name identified' },
          { key: 'labeler_country_matches_coo', label: 'Labeler country signal matches identified COO' },
          { key: 'product_marketing_status_active', label: 'Product marketing status is active' },
        ],
      },
      {
        name: 'FDA Import Refusals',
        category: 'structured',
        factors: [
          { key: 'import_refusal_found', label: 'Import refusal record found for this product/labeler' },
          { key: 'refusal_is_recent', label: 'Import refusal is recent (within lookback window)' },
          { key: 'refusal_country_identified', label: 'Refusal origin country identified' },
          { key: 'import_refusals_signal_strength', label: 'Import refusals signal strength (multiple records)' },
        ],
      },
      {
        name: 'EPA TRI',
        category: 'structured',
        factors: [
          { key: 'tri_facility_found', label: 'TRI facility found for manufacturer' },
          { key: 'tri_has_pharma_naics', label: 'Facility has pharmaceutical NAICS code (3254)' },
          { key: 'tri_location_matches_coo', label: 'Facility location matches identified COO' },
          { key: 'tri_chemical_data_consistent', label: 'Chemical release data consistent with manufacturing activity' },
        ],
      },
      {
        name: 'FDA Drug Shortages',
        category: 'structured',
        factors: [
          { key: 'shortage_record_found', label: 'Shortage record found for this product/drug' },
          { key: 'shortage_references_manufacturing_location', label: 'Shortage reason references a manufacturing facility location' },
          { key: 'shortage_country_matches_coo', label: 'Manufacturing facility country in shortage record matches identified COO' },
        ],
      },
      {
        name: 'FDA Recalls / Enforcement',
        category: 'structured',
        factors: [
          { key: 'recall_records_found', label: 'Recall records found for this product' },
          { key: 'recall_identifies_manufacturer', label: 'Recall identifies manufacturer/facility' },
          { key: 'recall_country_matches_coo', label: 'Manufacturer country in recall matches identified COO' },
          { key: 'recall_references_manufacturing_location', label: 'Product description references manufacturing location' },
        ],
      },
      {
        name: 'Drugs@FDA',
        category: 'structured',
        factors: [
          { key: 'drug_application_found', label: 'Drug application found in Drugs@FDA database' },
          { key: 'application_type_identified', label: 'Application type identified (NDA, ANDA, BLA)' },
          { key: 'application_country_matches_coo', label: 'Application country signal matches identified COO' },
          { key: 'application_status_active', label: 'Application status is active/approved' },
        ],
      },
      {
        name: 'FDA Registration',
        category: 'structured',
        factors: [
          { key: 'registration_found', label: 'FDA site registration found for manufacturer' },
          { key: 'registration_country_identified', label: 'Registration country identified' },
          { key: 'registration_country_matches_coo', label: 'Registration country matches identified COO' },
          { key: 'orange_book_listing_found', label: 'Orange Book listing found for this product' },
        ],
      },
      {
        name: 'WHO Prequalification',
        category: 'structured',
        factors: [
          { key: 'who_pq_listing_found', label: 'WHO Prequalification listing found' },
          { key: 'who_pq_manufacturing_site_identified', label: 'WHO PQ manufacturing site identified' },
          { key: 'who_pq_country_matches_coo', label: 'WHO PQ manufacturing country matches identified COO' },
        ],
      },
      {
        name: 'DMF / PharmaCompass',
        category: 'structured',
        factors: [
          { key: 'dmf_record_found', label: 'Drug Master File record found' },
          { key: 'dmf_manufacturing_site_identified', label: 'DMF manufacturing site identified' },
          { key: 'dmf_country_matches_coo', label: 'DMF manufacturing country matches identified COO' },
          { key: 'pharmacompass_data_available', label: 'PharmaCompass API source data available' },
        ],
      },
      {
        name: 'FDA Label PDF',
        category: 'structured',
        factors: [
          { key: 'fda_label_pdf_found', label: 'FDA label PDF document found' },
          { key: 'fda_label_manufacturer_identified', label: 'Manufacturer identified from FDA label PDF' },
          { key: 'fda_label_country_identified', label: 'Manufacturing country identified from FDA label PDF' },
          { key: 'fda_label_country_matches_coo', label: 'FDA label country matches identified COO' },
        ],
      },
      {
        name: 'CDSCO / WHO GMP',
        category: 'structured',
        factors: [
          { key: 'cdsco_who_gmp_site_found', label: 'CDSCO/WHO GMP manufacturing site found' },
          { key: 'cdsco_who_gmp_country_india', label: 'GMP site is located in India' },
          { key: 'cdsco_who_gmp_country_matches_coo', label: 'GMP site country matches identified COO' },
        ],
      },
      {
        name: 'Health Canada DPD',
        category: 'structured',
        factors: [
          { key: 'health_canada_dpd_company_found', label: 'Company found in Health Canada Drug Product Database' },
          { key: 'health_canada_dpd_country_identified', label: 'Manufacturing country identified from Health Canada DPD' },
          { key: 'health_canada_dpd_country_matches_coo', label: 'Health Canada DPD country matches identified COO' },
        ],
      },
      {
        name: 'FDA Imports Corroboration',
        category: 'structured',
        factors: [
          { key: 'fda_imports_shipments_found', label: 'FDA import shipment records found' },
          { key: 'fda_imports_dominant_country_identified', label: 'Dominant import origin country identified' },
          { key: 'fda_imports_country_matches_coo', label: 'Import origin country matches identified COO' },
          { key: 'fda_imports_signal_strength', label: 'Import signal strength (volume of corroborating records)' },
        ],
      },
      // ── 3 AI-Optional sources (can be disabled per product) ──
      {
        name: 'DailyMed SPL + AI Analysis',
        category: 'ai-dependent',
        factors: [
          { key: 'spl_contains_manufacturing_info', label: 'SPL document contains manufacturing information' },
          { key: 'spl_country_identified', label: 'Manufacturing country identified from SPL content' },
          { key: 'spl_country_matches_coo', label: 'SPL manufacturing country matches identified COO' },
          { key: 'spl_manufacturer_name_identified', label: 'Manufacturer name identified from SPL content' },
          { key: 'spl_manufacturing_context_clear', label: 'SPL manufacturing context is clear and unambiguous' },
        ],
      },
      {
        name: 'Package Image OCR',
        category: 'ai-dependent',
        factors: [
          { key: 'package_image_found', label: 'Package image found for this product' },
          { key: 'package_text_extracted', label: 'Text extracted from package image' },
          { key: 'package_country_identified', label: 'Manufacturing country identified from package text' },
          { key: 'package_country_matches_coo', label: 'Package manufacturing country matches identified COO' },
          { key: 'package_manufacturing_context_clear', label: 'Package manufacturing context is clear and unambiguous' },
        ],
      },
      {
        name: 'SEC EDGAR 10-K',
        category: 'ai-dependent',
        factors: [
          { key: 'sec_filing_found', label: 'SEC filing found for this company' },
          { key: 'sec_manufacturing_section_found', label: 'Manufacturing section found in SEC filing' },
          { key: 'sec_country_identified', label: 'Manufacturing country identified from SEC filing' },
          { key: 'sec_country_matches_coo', label: 'SEC manufacturing country matches identified COO' },
          { key: 'sec_manufacturing_details_clear', label: 'SEC manufacturing facility details are clear' },
        ],
      },
    ],
  },
  qrs: {
    display: 'Quality Rating Agent',
    type: 'Supply Chain Risk Score',
    description: 'Additive composite risk scorer (Risk Scorer V1) — baseline 40, 5 sections with caps. Aggregates COO confidence, CCP flags, FDA recalls, import refusals, drug shortages, GMP certifications, and US manufacturing signals. CCP hard floor enforced in code. Risk levels: HIGH >= 70, MEDIUM 40-69, LOW <= 39.',
    dataSources: [
      {
        name: 'A — Transparency & COO (cap +35)',
        category: 'structured',
        factors: [
          { key: 'coo_confidence_penalty', label: 'COO confidence penalty — (100 - score) × 0.25, max +25' },
          { key: 'coo_contradictions', label: 'COO contradictions — MODERATE +15, LOW tier +30' },
          { key: 'repackager_detected', label: 'Repackager detected as labeler (+25)' },
          { key: 'deception_flag', label: 'Deception flag — label COO conflicts with import override (+20)' },
          { key: 'api_vs_fd_divergence', label: 'API vs finished-dose country divergence (+15)' },
        ],
      },
      {
        name: 'B — Geopolitics & Compliance (cap +40)',
        category: 'structured',
        factors: [
          { key: 'ccp', label: 'CCP affiliation — Level 1: +30 / floor 70 · Level 2+: +55 / floor 85' },
          { key: 'high_risk_country', label: 'High-risk country (China, Russia, Iran, NK, Belarus) (+15)' },
          { key: 'fda_import_refusals', label: 'FDA import refusals <24 months (+10/refusal, cap +35)' },
        ],
      },
      {
        name: 'C — Quality & Continuity (cap +35)',
        category: 'structured',
        factors: [
          { key: 'fda_recall', label: 'FDA recall — Class I +35 / Class II +20 / Class III +8 (×1.5 if <12 mo)' },
          { key: 'drug_shortage', label: 'Drug shortage — Current/Limited +30 · Anticipated +15' },
          { key: 'import_corroboration_contradiction', label: 'Import corroboration contradicts COO — weak +5, moderate +10' },
        ],
      },
      {
        name: 'D — Quality Attenuators (cap -30)',
        category: 'structured',
        factors: [
          { key: 'who_prequalification', label: 'WHO Prequalification match (-18)' },
          { key: 'gmp', label: 'GMP certification — CDSCO or EudraGMDP: -12 · Both: -15' },
        ],
      },
      {
        name: 'E — Domestic US Bonus (cap -25, mutually exclusive with CCP)',
        category: 'structured',
        factors: [
          { key: 'sec_edgar_us', label: 'SEC EDGAR confirms US manufacturing (-15)' },
          { key: 'epa_tri_pharma', label: 'EPA TRI US pharma facility confirmed (-10)' },
          { key: 'import_corr_strong_us', label: 'Import corroboration strong US (>=80% + >=10 shipments) (-12)' },
        ],
      },
    ],
  },
  pis: {
    display: 'Pricing Intelligence Agent',
    type: 'Pricing Intelligence Score',
    description: 'Additive pricing scoring — starts at 0%, adds per factor found across 5 data sources (24 factors). Assesses price availability, cost position, formulary access, and market competition. Tiers: HIGH >= 70, MODERATE 40-69, LOW < 40.',
    dataSources: [
      {
        name: 'CMS NADAC Pricing',
        category: 'structured',
        factors: [
          { key: 'nadac_price_available', label: 'NADAC price data available for this NDC' },
          { key: 'nadac_price_below_class_median', label: 'NADAC price below therapeutic class median' },
          { key: 'nadac_price_stable_90_days', label: 'NADAC price stable over 90 days (< 10% change)' },
          { key: 'nadac_price_decrease_trend', label: 'NADAC price showing decrease trend' },
          { key: 'nadac_effective_date_current', label: 'NADAC effective date is current' },
          { key: 'nadac_classification_matches', label: 'NADAC classification for rate setting matches' },
        ],
      },
      {
        name: 'AWP (Average Wholesale Price)',
        category: 'structured',
        factors: [
          { key: 'awp_price_available', label: 'AWP price data available' },
          { key: 'awp_to_nadac_ratio_reasonable', label: 'AWP-to-NADAC ratio is reasonable' },
          { key: 'awp_below_class_median', label: 'AWP below therapeutic class median' },
          { key: 'awp_published_recently', label: 'AWP published recently' },
        ],
      },
      {
        name: 'FSS (Federal Supply Schedule / VA)',
        category: 'structured',
        factors: [
          { key: 'fss_contract_active', label: 'Active FSS/VA contract exists' },
          { key: 'fss_price_available', label: 'FSS price data available' },
          { key: 'fss_price_below_nadac', label: 'FSS price is below NADAC' },
          { key: 'fss_competitive_tier', label: 'FSS price is in competitive tier' },
        ],
      },
      {
        name: 'Formulary Coverage',
        category: 'structured',
        factors: [
          { key: 'on_major_pbm_formulary', label: 'On major PBM formulary' },
          { key: 'preferred_tier_status', label: 'Has preferred tier status' },
          { key: 'no_prior_authorization_required', label: 'No prior authorization required' },
          { key: 'step_therapy_not_required', label: 'Step therapy not required' },
          { key: 'generic_alternative_available', label: 'Generic alternative available' },
        ],
      },
      {
        name: 'Market Supply Analysis',
        category: 'structured',
        factors: [
          { key: 'domestic_manufacturer_available', label: 'Domestic manufacturer available' },
          { key: 'multiple_suppliers_exist', label: 'Multiple suppliers exist' },
          { key: 'no_sole_source_designation', label: 'No sole-source designation' },
          { key: 'price_premium_under_50_pct', label: 'Domestic price premium under 50%' },
          { key: 'therapeutic_alternatives_exist', label: 'Therapeutic alternatives exist' },
        ],
      },
    ],
  },
  coors: {
    display: 'COORS Agent',
    type: 'Country of Origin Risk Score',
    description: 'Additive geopolitical risk scoring — starts at 0%, adds per risk factor found (24 factors). Uses cached country risk profiles from World Bank, USTR, and FDA data. Higher score = higher risk. Tiers: HIGH risk >= 70, MODERATE 40-69, LOW < 40.',
    dataSources: [
      {
        name: 'Geopolitical Stability',
        category: 'structured',
        factors: [
          { key: 'country_under_sanctions', label: 'Country is under active sanctions' },
          { key: 'active_trade_dispute', label: 'Active trade dispute with the US' },
          { key: 'political_instability_index_high', label: 'Political instability index is high' },
          { key: 'state_controlled_pharma_sector', label: 'State-controlled pharmaceutical sector' },
          { key: 'recent_diplomatic_tensions', label: 'Recent diplomatic tensions with the US' },
        ],
      },
      {
        name: 'Trade Relationship',
        category: 'structured',
        factors: [
          { key: 'unfavorable_trade_agreement', label: 'Unfavorable trade agreement terms' },
          { key: 'import_tariffs_above_10pct', label: 'Import tariffs above 10%' },
          { key: 'trade_deficit_in_pharma', label: 'Trade deficit in pharmaceutical sector' },
          { key: 'customs_processing_delays', label: 'Customs processing delays reported' },
          { key: 'export_restrictions_active', label: 'Export restrictions currently active' },
        ],
      },
      {
        name: 'Regulatory Quality',
        category: 'structured',
        factors: [
          { key: 'no_mutual_recognition_agreement', label: 'No mutual recognition agreement with FDA' },
          { key: 'weak_gmp_enforcement', label: 'Weak GMP enforcement in country' },
          { key: 'fda_import_alerts_active', label: 'FDA import alerts active for this country' },
          { key: 'regulatory_framework_score_low', label: 'Regulatory framework quality score is low' },
          { key: 'no_inspection_agreement', label: 'No FDA inspection agreement in place' },
        ],
      },
      {
        name: 'IP Protection',
        category: 'structured',
        factors: [
          { key: 'weak_ip_enforcement', label: 'Weak intellectual property enforcement' },
          { key: 'patent_infringement_history', label: 'History of patent infringement' },
          { key: 'ustr_special_301_listed', label: 'Listed on USTR Special 301 report' },
          { key: 'counterfeit_risk_elevated', label: 'Elevated counterfeit drug risk' },
        ],
      },
      {
        name: 'Supply Concentration Risk',
        category: 'structured',
        factors: [
          { key: 'single_source_country', label: 'Single-source country dependency' },
          { key: 'api_concentration_above_50pct', label: 'API concentration above 50% from this country' },
          { key: 'limited_alternative_suppliers', label: 'Limited alternative suppliers available' },
          { key: 'critical_drug_sole_source', label: 'Critical drug with sole-source designation' },
          { key: 'supply_chain_depth_shallow', label: 'Supply chain depth is shallow' },
        ],
      },
    ],
  },
  pns: {
    display: 'Political Nexus Agent',
    type: 'Political Nexus Score',
    description: 'Additive scoring model — starts at 0%, adds evenly-weighted factor hits. Currently 5 factors at 20% each; weights auto-adjust as new data sources/factors are added. READER ONLY: queries existing metasupply tables, no external API calls.',
    dataSources: [
      {
        name: 'Political Donations',
        category: 'structured',
        factors: [
          { key: 'political_donations', label: '>= 1 record found in executive_political_donations table' },
        ],
      },
      {
        name: 'PAC Contributions',
        category: 'structured',
        factors: [
          { key: 'pac_contributions', label: '>= 1 record found in pac_contributions table' },
        ],
      },
      {
        name: 'Lobbying Filings',
        category: 'structured',
        factors: [
          { key: 'lobbying_filings', label: '>= 1 record found in manufacturer_lobbying_filings table' },
        ],
      },
      {
        name: 'Stock Holdings',
        category: 'structured',
        factors: [
          { key: 'stock_holdings', label: '>= 1 record found in politician_stock_holdings table' },
        ],
      },
      {
        name: 'CCP Connections',
        category: 'structured',
        factors: [
          { key: 'ccp_connections', label: '>= 1 record in ccp_matches table or ccp_flag set' },
        ],
      },
    ],
  },
  ndc_lookup: {
    display: 'NDC Lookup Agent',
    type: 'FDA NDC Metadata',
    description: 'Enriches product records with metadata from the FDA NDC Directory and RxNorm API. Lookup agent — no scoring, passes enriched data through. Rate limits: FDA 240 req/min (with API key), RxNorm 20 req/sec.',
    dataSources: [
      {
        name: 'FDA NDC Directory',
        category: 'structured',
        factors: [
          { key: 'brand_name', label: 'Brand name resolved from FDA NDC directory' },
          { key: 'generic_name', label: 'Generic name resolved' },
          { key: 'labeler_name', label: 'Labeler/manufacturer name identified' },
          { key: 'dosage_form', label: 'Dosage form identified (tablet, capsule, etc.)' },
          { key: 'strength', label: 'Active ingredient strength resolved' },
          { key: 'route', label: 'Route of administration identified' },
          { key: 'pharm_class', label: 'Pharmacological class resolved' },
          { key: 'product_type', label: 'Product type identified (human Rx, OTC, etc.)' },
          { key: 'marketing_category', label: 'Marketing category resolved (NDA, ANDA, etc.)' },
          { key: 'application_number', label: 'FDA application number linked' },
        ],
      },
      {
        name: 'RxNorm',
        category: 'structured',
        factors: [
          { key: 'rxcui', label: 'RxNorm Concept Unique Identifier (RXCUI) resolved' },
          { key: 'rxnorm_status', label: 'RxNorm NDC status determined' },
          { key: 'rxnorm_concept_name', label: 'RxNorm clinical drug concept name resolved' },
        ],
      },
    ],
  },
  steward_country_normalizer: {
    display: 'Normalizer',
    type: 'Data Quality',
    description: 'Rule-based data normalization agent. Currently normalizes products.country_of_origin — detects US state codes, abbreviation prefixes, garbage values, and canonical corrections. Resets COO scoring metadata when garbage is nulled. Zero LLM cost.',
    dataSources: [
      {
        name: 'Country Normalization',
        category: 'structured',
        factors: [
          { key: 'country_normalization', label: 'Country of origin value was normalized or nulled' },
        ],
      },
    ],
  },
  steward_manufacturer_name_geo: {
    display: 'Manufacturer Geo',
    type: 'Manufacturer Intelligence',
    description: 'Detects Chinese geographic keywords (province names, pharma hub cities) in manufacturer names from steward evidence and the manufacturers table. Feeds the Manufacturer_Name_Geo voting category in the combined COO scoring model.',
    dataSources: [
      {
        name: 'Manufacturer Name Analysis',
        category: 'structured',
        factors: [
          { key: 'manufacturer_name_geo', label: 'Chinese geographic keyword detected in manufacturer name' },
        ],
      },
    ],
  },
};

// ============================================================
// HELPERS
// ============================================================

function formatTimeAgo(iso: string | null): string {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Unknown';
  const diffMs = Date.now() - t;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return `${Math.floor(diffH / 24)}d ago`;
}

// ============================================================
// COMPONENT
// ============================================================

export default function AgentDetail() {
  const { agentName } = useParams<{ agentName: string }>();
  const navigate = useNavigate();
  const { T } = useTheme();
  const { isMobile } = useResponsive();

  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);
  const [agentHealth, setAgentHealth] = useState<AgentHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);

  // Product search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [rescoreStatus, setRescoreStatus] = useState<Record<string, 'idle' | 'loading' | 'success' | 'error'>>({});

  // Data source toggle state (local only — persisted when swarm is live)
  const [disabledSources, setDisabledSources] = useState<Set<string>>(new Set());
  // Expanded data source sections
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  // Distinct product count for steward agents (from steward_evidence)
  const [stewardProductCount, setStewardProductCount] = useState<number | null>(null);

  const isSteward = (agentName || '').startsWith('steward_');
  const stewardMeta = STEWARD_META[agentName || ''];
  const meta = AGENT_META[agentName || ''] || {
    display: stewardMeta?.display || (agentName || '').replace(/_/g, ' '),
    type: stewardMeta?.type || 'Agent',
    description: stewardMeta?.description || 'Agent details not available.',
    dataSources: [],
  };

  const fetchAgentData = useCallback(async () => {
    if (!agentName) return;
    try {
      const [statusRes, healthRes] = await Promise.allSettled([
        fetch(`${API_BASE}/api/metasupply/swarm/status`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/metasupply/swarm/health`, { credentials: 'include' }),
      ]);

      if (statusRes.status === 'fulfilled' && statusRes.value.ok) {
        const data = await statusRes.value.json();
        const agent = data.agents?.find((a: AgentStatus) => a.agent_id === agentName);
        if (agent) setAgentStatus(agent);
      }
      if (healthRes.status === 'fulfilled' && healthRes.value.ok) {
        const data = await healthRes.value.json();
        if (data.agents?.[agentName]) {
          setAgentHealth(data.agents[agentName]);
        }
      }
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch agent data');
    } finally {
      setLoading(false);
    }
  }, [agentName]);

  useEffect(() => {
    fetchAgentData();
    const interval = setInterval(fetchAgentData, 30000);
    return () => clearInterval(interval);
  }, [fetchAgentData]);

  // Fetch distinct product count for steward agents
  useEffect(() => {
    if (!isSteward || !agentName) return;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/metasupply/swarm/agents/${agentName}/scored-products`, { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          setStewardProductCount(data.count ?? 0);
        }
      } catch {
        // Non-critical
      }
    })();
  }, [isSteward, agentName]);

  // Toggle agent enable/disable
  const toggleAgent = async () => {
    if (!agentName || toggling) return;
    setToggling(true);
    try {
      const action = agentStatus?.enabled ? 'disable' : 'enable';
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/agents/${agentName}/${action}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (resp.ok) {
        await fetchAgentData();
      } else {
        const errData = await resp.json().catch(() => ({ detail: 'Unknown error' }));
        setError(errData.detail || `Failed to ${action} agent`);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to toggle agent');
    } finally {
      setToggling(false);
    }
  };

  // Search products
  const [searchError, setSearchError] = useState<string | null>(null);
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchError(null);
    try {
      const resp = await fetch(
        `${API_BASE}/metasupply/search?q=${encodeURIComponent(searchQuery.trim())}&limit=20`,
        { credentials: 'include' },
      );
      if (resp.ok) {
        const data = await resp.json();
        // Backend returns { products: { results: [...] }, clinical_drugs: { results: [...] }, ... }
        const rawProducts = Array.isArray(data.products)
          ? data.products
          : Array.isArray(data.products?.results)
            ? data.products.results
            : data.results || [];
        const products = rawProducts.map((p: Record<string, unknown>) => ({
          ...p,
          manufacturer_name: p.manufacturer_name || p.manufacturer || '--',
        }));
        setSearchResults(products);
        if (products.length === 0) setSearchError('No products found. Try a different search term.');
      } else {
        setSearchError('Search failed — server returned an error.');
        setSearchResults([]);
      }
    } catch (err) {
      const msg = err instanceof TypeError
        ? 'Search failed — unexpected response format.'
        : 'Search failed — could not reach the server.';
      setSearchError(msg);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Per-product disabled sources: Map<productId, Set<sourceName>>
  const [productDisabledSources, setProductDisabledSources] = useState<Record<number, Set<string>>>({});
  const [editingProductId, setEditingProductId] = useState<number | null>(null);

  const toggleProductSource = (productId: number, sourceName: string) => {
    setProductDisabledSources(prev => {
      const current = prev[productId] || new Set<string>();
      const next = new Set(current);
      if (next.has(sourceName)) next.delete(sourceName);
      else next.add(sourceName);
      return { ...prev, [productId]: next };
    });
  };

  // Trigger rescore for a product
  const triggerRescore = async (ndcCode: string) => {
    setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'loading' }));
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ndcs: [ndcCode],
          agents: agentName ? [agentName] : undefined,
          priority: 'high',
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.triggered_agents && data.triggered_agents.length > 0) {
          // Agent is actually running — keep "Scoring..." visible, then refresh
          setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'loading' }));
          // Poll for completion: check every 3s for up to 30s
          let attempts = 0;
          const poll = setInterval(async () => {
            attempts++;
            await fetchAgentData();
            if (attempts >= 10) {
              clearInterval(poll);
              setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'success' }));
              setTimeout(() => setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'idle' })), 5000);
            }
          }, 3000);
          // Also set success after a brief delay for immediate visual feedback
          setTimeout(() => {
            clearInterval(poll);
            fetchAgentData();
            setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'success' }));
            setTimeout(() => setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'idle' })), 5000);
          }, 15000);
        } else {
          // No agents were triggered (disabled, already running, etc.)
          const skipped = data.skipped_agents || [];
          const reason = skipped.length > 0 ? skipped[0].reason : 'unknown';
          if (reason === 'disabled') {
            setSearchError('Agent must be enabled before re-scoring. Click "Enable Agent" above.');
          }
          setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'error' }));
          setTimeout(() => setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'idle' })), 5000);
        }
      } else {
        setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'error' }));
        setTimeout(() => setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'idle' })), 5000);
      }
    } catch {
      setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'error' }));
      setTimeout(() => setRescoreStatus(prev => ({ ...prev, [ndcCode]: 'idle' })), 5000);
    }
  };

  const isEnabled = agentStatus?.enabled ?? false;
  const status = agentStatus ? (isEnabled ? (agentStatus.last_run_status || 'idle') : 'disabled') : 'offline';

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTopColor: T.gold, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <span style={{ fontSize: 14, color: T.textLight }}>Loading agent details...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Back link + Header */}
      <div>
        <button
          onClick={() => navigate('/agent-activity')}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            color: T.navyLight, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Agent Activity Dashboard
        </button>

        <div style={{
          background: T.offWhite, border: `1px solid ${T.border}`,
          borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 20,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.textDark, margin: 0, fontFamily: "'Playfair Display', Georgia, serif" }}>
                {meta.display}
              </h1>
              <span style={{
                fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
                padding: '4px 12px', borderRadius: 20,
                background: status === 'disabled' || status === 'offline' ? `${T.navy}10` : status === 'error' || status === 'failed' ? `${T.danger}15` : `${T.success}15`,
                color: status === 'disabled' || status === 'offline' ? T.textLight : status === 'error' || status === 'failed' ? T.danger : T.success,
              }}>
                {status.replace(/_/g, ' ')}
              </span>
            </div>
            <p style={{ fontSize: 14, color: T.textMid, maxWidth: 600, lineHeight: 1.6, margin: 0 }}>
              {meta.description}
            </p>
          </div>

          {/* Enable/Disable Toggle */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Agent Control
            </span>
            <button
              onClick={toggleAgent}
              disabled={toggling}
              style={{
                padding: '10px 28px', borderRadius: 8, border: 'none',
                background: isEnabled ? T.danger : T.success,
                color: T.white, fontSize: 13, fontWeight: 700, cursor: toggling ? 'wait' : 'pointer',
                fontFamily: 'inherit', opacity: toggling ? 0.7 : 1, transition: 'all 0.2s',
                minWidth: 140,
              }}
            >
              {toggling ? 'Processing...' : isEnabled ? 'Disable Agent' : 'Enable Agent'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: T.radiusSm, background: T.dangerBg, border: `1px solid ${T.danger}30`, color: T.danger, fontSize: 13, fontWeight: 600 }}>
          {error}
        </div>
      )}

      {/* Agent Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16 }}>
        {[
          { label: isSteward ? 'Total Products' : 'Total Products Scored', value: (isSteward && stewardProductCount !== null ? stewardProductCount : (agentHealth?.products_processed_total ?? agentStatus?.total_products_processed ?? 0)).toLocaleString(), link: isSteward ? `/agent-detail/${agentName}/total-products` : undefined },
          { label: 'Last Run', value: formatTimeAgo(agentStatus?.last_run_at ?? null) },
          { label: 'Error Rate', value: `${agentHealth?.error_rate ?? 0}%` },
          { label: 'Total Runs', value: String(agentStatus?.total_runs ?? 0) },
        ].map(stat => (
          <div
            key={stat.label}
            onClick={stat.link ? () => navigate(stat.link!) : undefined}
            style={{
              background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
              padding: 20, boxShadow: T.shadow,
              cursor: stat.link ? 'pointer' : 'default',
              transition: stat.link ? 'all 0.2s' : 'none',
            }}
            onMouseEnter={stat.link ? e => { (e.currentTarget as HTMLDivElement).style.borderColor = T.gold; } : undefined}
            onMouseLeave={stat.link ? e => { (e.currentTarget as HTMLDivElement).style.borderColor = T.border; } : undefined}
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: stat.link ? T.gold : T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              {stat.label}
              {stat.link && (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4, verticalAlign: 'middle' }}>
                  <path d="M9 18l6-6-6-6" />
                </svg>
              )}
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: T.textDark }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Quick Navigation: Products Scored & Failed Scoring */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
        <button
          onClick={() => navigate(isSteward ? `/agent-detail/${agentName}/product-updates` : `/agent-detail/${agentName}/scored-products`)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderRadius: T.radius,
            border: `1px solid ${T.border}`, background: T.white,
            cursor: 'pointer', boxShadow: T.shadow, transition: 'all 0.2s',
            fontFamily: 'inherit',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.success; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.border; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `${T.success}15`,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>{isSteward ? 'Product Updates' : 'Products Scored'}</div>
              <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{isSteward ? 'Logs the intelligence agent\'s product data collection for each product run' : 'View all successfully rescored products'}</div>
            </div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        <button
          onClick={() => navigate(isSteward ? `/agent-detail/${agentName}/failed-updates` : `/agent-detail/${agentName}/failed-scoring`)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderRadius: T.radius,
            border: `1px solid ${T.border}`, background: T.white,
            cursor: 'pointer', boxShadow: T.shadow, transition: 'all 0.2s',
            fontFamily: 'inherit',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.danger; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.border; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `${T.danger}15`,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>{isSteward ? 'Failed Updates' : 'Failed Scoring'}</div>
              <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{isSteward ? 'Logs the intelligence agent\'s product data collection that fails for a product run' : 'View products that failed during rescoring'}</div>
            </div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Evidence Definitions & Data Analyst — conditional for steward vs scoring agents */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (isSteward ? '1fr' : '1fr 1fr'), gap: 16 }}>
        {/* Evidence Definitions — hidden for data stewards */}
        {!isSteward && (
          <button
            onClick={() => navigate(`/agent-detail/${agentName}/evidence-definitions`)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderRadius: T.radius,
              border: `1px solid ${T.border}`, background: T.white,
              cursor: 'pointer', boxShadow: T.shadow, transition: 'all 0.2s',
              fontFamily: 'inherit',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.navy; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.border; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${T.navy}15`,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>Evidence Definitions</div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>View how each factor proves or disproves COO accuracy</div>
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        )}

        {/* Data Analyst — different content for stewards vs scoring agents */}
        <button
          onClick={() => navigate(isSteward ? `/steward-data-analyst/${agentName}` : `/data-analyst/${agentName}`)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderRadius: T.radius,
            border: `1px solid ${T.border}`, background: T.white,
            cursor: 'pointer', boxShadow: T.shadow, transition: 'all 0.2s',
            fontFamily: 'inherit',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.gold; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.border; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `${T.gold}15`,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>Data Analyst</div>
              <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                {isSteward
                  ? 'Discrepancies, rescrape flags, logic adjustments, and data completeness'
                  : 'Confidence overrides, reconciler conflicts, and aggregated intelligence'
                }
              </div>
            </div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Data Sources & Scoring Factors — hidden for data stewards */}
      {!isSteward && (() => {
        const structuredSources = meta.dataSources.filter(s => s.category === 'structured');
        const aiSources = meta.dataSources.filter(s => s.category === 'ai-dependent');
        const uncategorized = meta.dataSources.filter(s => !s.category);
        const hasCategories = structuredSources.length > 0 || aiSources.length > 0;

        const renderSourceCard = (source: DataSourceDef, idx: number) => {
          const isDisabled = disabledSources.has(source.name);
          const isExpanded = expandedSources.has(source.name);
          const hasFactors = source.factors.length > 0;

          return (
            <div key={source.name} style={{
              borderRadius: T.radiusSm,
              border: `1px solid ${isDisabled ? T.border : T.borderLight}`,
              background: isDisabled ? T.offWhite : T.white,
              opacity: isDisabled ? 0.6 : 1,
              transition: 'all 0.2s',
              overflow: 'hidden',
            }}>
              {/* Source header row */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', cursor: hasFactors ? 'pointer' : 'default',
              }}
                onClick={() => {
                  if (!hasFactors) return;
                  setExpandedSources(prev => {
                    const next = new Set(prev);
                    if (next.has(source.name)) next.delete(source.name);
                    else next.add(source.name);
                    return next;
                  });
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: isDisabled ? T.textLight : T.success,
                  }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>{`Source ${idx + 1}: ${source.name}`}</span>
                  {hasFactors && (
                    <span style={{ fontSize: 11, color: T.textLight, fontWeight: 500 }}>
                      ({source.factors.length} factors)
                    </span>
                  )}
                  {hasFactors && (
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2"
                      style={{
                        transition: 'transform 0.2s',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        marginLeft: 4,
                      }}
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDisabledSources(prev => {
                      const next = new Set(prev);
                      if (next.has(source.name)) next.delete(source.name);
                      else next.add(source.name);
                      return next;
                    });
                  }}
                  style={{
                    padding: '5px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                    border: `1px solid ${isDisabled ? T.success : T.danger}30`,
                    background: isDisabled ? `${T.success}12` : `${T.danger}12`,
                    color: isDisabled ? T.success : T.danger,
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                    flexShrink: 0,
                  }}
                >
                  {isDisabled ? 'Enable' : 'Disable'}
                </button>
              </div>

              {/* Expanded factors list */}
              {isExpanded && hasFactors && (
                <div style={{
                  borderTop: `1px solid ${T.borderLight}`,
                  padding: '12px 16px 16px 38px',
                  background: T.offWhite,
                }}>
                  {source.factors.map(factor => (
                    <div key={factor.key} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      padding: '6px 0',
                      fontSize: 12, color: T.textMid, lineHeight: 1.5,
                    }}>
                      <span style={{
                        display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                        background: T.gold, marginTop: 5, flexShrink: 0,
                      }} />
                      <div>
                        <span style={{ fontWeight: 600, color: T.textDark, fontFamily: 'monospace', fontSize: 11 }}>
                          {factor.key}
                        </span>
                        <span style={{ color: T.textLight, margin: '0 6px' }}>&mdash;</span>
                        <span>{factor.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        };

        // Track source index globally across categories
        let globalIdx = 0;

        return (
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
            <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0, marginBottom: 16 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: '-2px', marginRight: 6 }}>
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              </svg>
              Data Sources &amp; Scoring Factors
            </h3>

            {meta.dataSources.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Structured Sources */}
                {hasCategories && structuredSources.length > 0 && (
                  <>
                    <div style={{
                      fontSize: 10, fontWeight: 800, color: T.navy, textTransform: 'uppercase',
                      letterSpacing: '0.1em', padding: '8px 0 4px', borderBottom: `2px solid ${T.navy}15`,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2.5">
                        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
                      </svg>
                      Structured Sources (always available)
                    </div>
                    {structuredSources.map(s => {
                      const el = renderSourceCard(s, globalIdx);
                      globalIdx++;
                      return el;
                    })}
                  </>
                )}

                {/* AI-Dependent Sources */}
                {hasCategories && aiSources.length > 0 && (
                  <>
                    <div style={{
                      fontSize: 10, fontWeight: 800, color: T.gold, textTransform: 'uppercase',
                      letterSpacing: '0.1em', padding: '12px 0 4px', borderBottom: `2px solid ${T.gold}25`,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2.5">
                        <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
                      </svg>
                      AI-Dependent Sources (optional, enable/disable per product)
                    </div>
                    {aiSources.map(s => {
                      const el = renderSourceCard(s, globalIdx);
                      globalIdx++;
                      return el;
                    })}
                  </>
                )}

                {/* Uncategorized sources (other agents) */}
                {uncategorized.map(s => {
                  const el = renderSourceCard(s, globalIdx);
                  globalIdx++;
                  return el;
                })}
              </div>
            ) : (
              <div style={{ padding: 16, textAlign: 'center', color: T.textLight, fontSize: 13 }}>
                No data source information available for this agent.
              </div>
            )}
          </div>
        );
      })()}

      {/* Product Search & Rescore — hidden for data stewards */}
      {!isSteward && (
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
        <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0, marginBottom: 16 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: '-2px', marginRight: 6 }}>
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          Search Products for Rescoring
        </h3>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            placeholder="Search by product name, NDC, or manufacturer..."
            style={{
              flex: 1, padding: '10px 14px', borderRadius: T.radiusSm,
              border: `1px solid ${T.border}`, fontSize: 13, fontFamily: 'inherit',
              outline: 'none', transition: 'border-color 0.2s',
            }}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor = T.navyLight; }}
            onBlur={e => { (e.target as HTMLInputElement).style.borderColor = T.border; }}
          />
          <button
            onClick={handleSearch}
            disabled={searching || !searchQuery.trim()}
            style={{
              padding: '10px 24px', borderRadius: T.radiusSm, border: 'none',
              background: T.navy, color: T.white, fontSize: 13, fontWeight: 700,
              cursor: searching ? 'wait' : 'pointer', fontFamily: 'inherit',
              opacity: searching || !searchQuery.trim() ? 0.6 : 1,
            }}
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['NDC', 'Product Name', 'Manufacturer', 'COO', 'Actions'].map(col => (
                    <th key={col} style={{
                      fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase',
                      letterSpacing: '0.06em', padding: '8px 12px', textAlign: 'left',
                      borderBottom: `1px solid ${T.border}`,
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {searchResults.map(product => {
                  const ndc = product.ndc_code;
                  const rStatus = rescoreStatus[ndc] || 'idle';
                  return (
                    <React.Fragment key={product.id}>
                    <tr style={{ transition: 'background 0.15s' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = T.offWhite; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                    >
                      <td style={{ fontSize: 12, fontWeight: 600, color: T.textDark, padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}`, fontVariantNumeric: 'tabular-nums' }}>
                        {ndc}
                      </td>
                      <td style={{ fontSize: 12, color: T.textMid, padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}`, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span
                          style={{ cursor: 'pointer', color: T.navyLight }}
                          onClick={() => navigate(`/products/${product.id}`)}
                        >
                          {product.product_name}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: T.textMid, padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}` }}>
                        {product.manufacturer_name || '--'}
                      </td>
                      <td style={{ fontSize: 12, padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}` }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: T.textMid }}>
                          {product.country_of_origin || product.api_source_country || 'Unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}`, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button
                            onClick={() => triggerRescore(ndc)}
                            disabled={rStatus === 'loading'}
                            style={{
                              padding: '4px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                              border: `1px solid ${rStatus === 'success' ? T.success : rStatus === 'error' ? T.danger : T.gold}40`,
                              background: rStatus === 'success' ? `${T.success}12` : rStatus === 'error' ? `${T.danger}12` : `${T.gold}12`,
                              color: rStatus === 'success' ? T.success : rStatus === 'error' ? T.danger : T.gold,
                              cursor: rStatus === 'loading' ? 'wait' : 'pointer', fontFamily: 'inherit',
                              opacity: rStatus === 'loading' ? 0.7 : 1,
                            }}
                          >
                            {rStatus === 'loading' ? 'Scoring...' : rStatus === 'success' ? 'Queued' : rStatus === 'error' ? 'Failed' : 'Re-score'}
                          </button>
                          <button
                            onClick={() => setEditingProductId(editingProductId === product.id ? null : product.id)}
                            style={{
                              padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                              border: `1px solid ${editingProductId === product.id ? T.navyLight : T.border}`,
                              background: editingProductId === product.id ? `${T.navyLight}12` : 'transparent',
                              color: editingProductId === product.id ? T.navyLight : T.textMid,
                              cursor: 'pointer', fontFamily: 'inherit',
                            }}
                          >
                            {editingProductId === product.id ? 'Close' : 'Sources'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Per-product source config row */}
                    {editingProductId === product.id && (
                      <tr>
                        <td colSpan={5} style={{ padding: '12px 16px', background: T.offWhite, borderBottom: `1px solid ${T.border}` }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                            Data Sources for {product.product_name}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {meta.dataSources.map(src => {
                              const isDisabled = productDisabledSources[product.id]?.has(src.name);
                              return (
                                <button
                                  key={src.name}
                                  onClick={() => toggleProductSource(product.id, src.name)}
                                  style={{
                                    padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                    border: `1px solid ${isDisabled ? T.danger : T.success}40`,
                                    background: isDisabled ? `${T.danger}08` : `${T.success}08`,
                                    color: isDisabled ? T.danger : T.success,
                                    cursor: 'pointer', fontFamily: 'inherit',
                                    display: 'flex', alignItems: 'center', gap: 5,
                                  }}
                                >
                                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: isDisabled ? T.danger : T.success, display: 'inline-block' }} />
                                  {src.name}
                                  <span style={{ fontSize: 9, opacity: 0.7 }}>({isDisabled ? 'OFF' : 'ON'})</span>
                                </button>
                              );
                            })}
                          </div>
                          <div style={{ fontSize: 10, color: T.textLight, marginTop: 8, fontStyle: 'italic' }}>
                            Toggle sources on/off for this product. Changes take effect on next re-score.
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {searchError && !searching && (
          <div style={{ padding: 16, textAlign: 'center', color: T.textLight, fontSize: 13 }}>
            {searchError}
          </div>
        )}
      </div>
      )}

      {/* Agent Health Details */}
      {agentHealth && (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0, marginBottom: 16 }}>
            Health &amp; Circuit Breaker
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
            <div style={{ padding: '12px 16px', borderRadius: T.radiusSm, border: `1px solid ${T.borderLight}` }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', marginBottom: 4 }}>Circuit Breaker</div>
              <div style={{
                fontSize: 15, fontWeight: 700,
                color: agentHealth.circuit_breaker_state === 'closed' ? T.success : agentHealth.circuit_breaker_state === 'open' ? T.danger : T.warning,
              }}>
                {agentHealth.circuit_breaker_state.toUpperCase()}
              </div>
            </div>
            <div style={{ padding: '12px 16px', borderRadius: T.radiusSm, border: `1px solid ${T.borderLight}` }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', marginBottom: 4 }}>Consecutive Failures</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: agentHealth.consecutive_failures > 0 ? T.danger : T.textDark }}>
                {agentHealth.consecutive_failures}
              </div>
            </div>
            <div style={{ padding: '12px 16px', borderRadius: T.radiusSm, border: `1px solid ${T.borderLight}` }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', marginBottom: 4 }}>Last Heartbeat</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark }}>
                {formatTimeAgo(agentHealth.last_heartbeat)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
